// Textfunk mit Kanalwahl (Konzept §9).
//
// Reichweite und Verstümmelung kommen vom Server — der Client zeigt nur an,
// was ankommt (channel, text, quality). Absender bleiben absichtlich unbekannt:
// fromId ist serverseitig null, und wir rendern auch keine Ersatz-ID.

import {
  RADIO_MIN_CHANNEL, RADIO_MAX_CHANNEL, RADIO_HISTORY, RADIO_MESSAGE_MAX_LEN,
} from '../../shared/constants.js';
import { rtcSignal } from '../../shared/protocol.js';

/** Anzeigedauer eines Spruchs im Funkfenster (Echtzeitsekunden). */
export const RADIO_MSG_TTL = 40;

// ---------------------------------------------------------------------------
// Reine Logik — headless testbar, kein DOM
// ---------------------------------------------------------------------------

/** 347 → „3,47" (Konzept §9: Frequenzen als Information, diese Schreibweise). */
export function formatChannel(channel) {
  const n = Math.round(Number(channel));
  if (!Number.isFinite(n)) return null;
  if (n < RADIO_MIN_CHANNEL || n > RADIO_MAX_CHANNEL) return null;
  const major = Math.floor(n / 100);
  const minor = n % 100;
  return `${major},${String(minor).padStart(2, '0')}`;
}

/**
 * „3,47" / „3.47" / „347" → 347. Ungültig → null.
 * Bewusst streng: außerhalb von RADIO_MIN..MAX und Müll werden abgewiesen.
 */
export function parseChannel(text) {
  if (text == null) return null;
  const s = String(text).trim();
  if (!s) return null;

  let n;
  if (/^\d{3}$/.test(s)) {
    n = Number(s);
  } else {
    const m = s.match(/^(\d)[,.](\d{1,2})$/);
    if (!m) return null;
    const major = Number(m[1]);
    const frac = m[2].length === 1 ? Number(m[2]) * 10 : Number(m[2]);
    n = major * 100 + frac;
  }

  if (!Number.isFinite(n)) return null;
  n = Math.round(n);
  if (n < RADIO_MIN_CHANNEL || n > RADIO_MAX_CHANNEL) return null;
  return n;
}

/** Ist der Eingabestring eine reine Kanalzahl (kein Freitext)? */
export function looksLikeChannel(text) {
  if (text == null) return false;
  const s = String(text).trim();
  return /^\d{3}$/.test(s) || /^\d[,.]\d{1,2}$/.test(s);
}

/**
 * Spruch in die Anzeigeliste legen. Pruned nach TTL und RADIO_HISTORY.
 * Mutiert `list` in-place, gibt die Liste zurück (keine Frame-Allok nötig
 * wenn der Aufrufer dieselbe Array-Referenz hält).
 */
export function pushRadioMessage(list, entry, nowSec, opts = {}) {
  const ttl = opts.ttl ?? RADIO_MSG_TTL;
  const max = opts.max ?? RADIO_HISTORY;
  list.push({
    text: entry.text,
    quality: entry.quality ?? 1,
    channel: entry.channel ?? 0,
    t: nowSec,
  });
  pruneRadioMessages(list, nowSec, ttl);
  while (list.length > max) list.shift();
  return list;
}

/** Alte Einträge verwerfen. */
export function pruneRadioMessages(list, nowSec, ttl = RADIO_MSG_TTL) {
  let i = 0;
  while (i < list.length && nowSec - list[i].t > ttl) i++;
  if (i > 0) list.splice(0, i);
  return list;
}

// ---------------------------------------------------------------------------
// WebRTC-Haken (Sprachfunk, später). Standard: deaktiviert.
// ---------------------------------------------------------------------------

/**
 * Signalling über net (MSG.RTC_SIGNAL / rtcSignal-Events).
 *
 * Noch nicht verdrahtet im Spielbetrieb:
 *   - Push-to-Talk (Taste halten → Mic-Track enablen)
 *   - Rauschsimulation auf dem Audiopfad (quality vom Server auf Gain/Filter)
 *   - Reichweiten-Gating durch den Server (wer überhaupt ein Offer bekommt)
 *   - getUserMedia / Mikrofonberechtigung
 *
 * `enabled = false` ⇒ handleSignal und ensurePeer tun nichts. So liegt der
 * Pfad bereit, ohne toten WebRTC-Code im Normalbetrieb.
 */
export class VoiceLink {
  constructor(net) {
    this.net = net;
    this.enabled = false;
    this.peers = new Map(); // peerId -> { pc, polite }
    this._unsub = null;
    this._localStream = null;
  }

  bind() {
    if (!this.net || this._unsub) return;
    const handler = (fromId, data) => this.handleSignal(fromId, data);
    this.net.on('rtcSignal', handler);
    // Connection speichert Handler dauerhaft; Flag reicht als Abschaltung.
    this._unsub = () => { /* Handler bleiben harmlos wegen enabled-Check */ };
  }

  async handleSignal(fromId, data) {
    if (!this.enabled || !data || !fromId) return;
    const peer = this._ensurePeer(fromId);
    if (!peer) return;
    const { pc } = peer;

    try {
      if (data.sdp) {
        const desc = data.sdp;
        if (desc.type === 'offer') {
          // Glare: bei kollidierenden Offers gewinnt die höhere Session-ID.
          if (pc.signalingState !== 'stable' && !peer.polite) return;
          await pc.setRemoteDescription(desc);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          this._send(fromId, { sdp: pc.localDescription });
        } else if (desc.type === 'answer') {
          await pc.setRemoteDescription(desc);
        }
      } else if (data.candidate) {
        try {
          await pc.addIceCandidate(data.candidate);
        } catch {
          // Kandidat vor Remote-Description: üblich, ignorieren.
        }
      } else if (data.bye) {
        this.closePeer(fromId);
      }
    } catch (err) {
      console.warn('[voice] Signalfehler', fromId, err);
    }
  }

  /** PeerConnection anlegen. Ohne enabled und ohne Mic-Stream: no-op. */
  _ensurePeer(peerId) {
    if (!this.enabled) return null;
    let peer = this.peers.get(peerId);
    if (peer) return peer;

    const RTCPeer = globalThis.RTCPeerConnection;
    if (!RTCPeer) return null;

    const pc = new RTCPeer({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    // Politeness aus ID-Vergleich — deterministisch ohne Extra-Handshake.
    const selfId = this.net?.sessionId ?? '';
    const polite = selfId < peerId;

    pc.onicecandidate = (ev) => {
      if (ev.candidate) this._send(peerId, { candidate: ev.candidate });
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        this.closePeer(peerId);
      }
    };
    // Remote-Audio würde hier an einen Audio-Element/Context gehängt —
    // bewusst leer, bis PTT und Rauschpfad existieren.
    pc.ontrack = () => {};

    if (this._localStream) {
      for (const track of this._localStream.getTracks()) {
        pc.addTrack(track, this._localStream);
      }
    }

    peer = { pc, polite };
    this.peers.set(peerId, peer);
    return peer;
  }

  async call(peerId) {
    if (!this.enabled) return;
    const peer = this._ensurePeer(peerId);
    if (!peer) return;
    const offer = await peer.pc.createOffer();
    await peer.pc.setLocalDescription(offer);
    this._send(peerId, { sdp: peer.pc.localDescription });
  }

  _send(toId, payload) {
    if (!this.net?.send) return;
    this.net.send(rtcSignal(toId, payload));
  }

  closePeer(id) {
    const peer = this.peers.get(id);
    if (!peer) return;
    try { peer.pc.close(); } catch { /* ignore */ }
    this.peers.delete(id);
  }

  dispose() {
    for (const id of [...this.peers.keys()]) this.closePeer(id);
    if (this._localStream) {
      for (const t of this._localStream.getTracks()) t.stop();
      this._localStream = null;
    }
  }
}

// ---------------------------------------------------------------------------
// RadioSystem
// ---------------------------------------------------------------------------

export class RadioSystem {
  async init(ctx) {
    this.ctx = ctx;
    this.powered = false;
    this.channel = 347; // gängiger „Marktplatz"-Start, spieler können scannen
    this.tuning = false;
    this.messages = [];
    this._lastDomSig = '';
    this._noiseLevel = 0;
    this._now = 0;

    this.root = document.getElementById('hud-radio');
    this.voice = new VoiceLink(ctx.net);
    this.voice.bind();

    if (this.root) {
      this.root.innerHTML = '';
      this.chEl = document.createElement('div');
      this.chEl.className = 'ch off';
      this.msgsEl = document.createElement('div');
      this.msgsEl.className = 'msgs';
      this.inputEl = document.createElement('input');
      this.inputEl.type = 'text';
      this.inputEl.maxLength = RADIO_MESSAGE_MAX_LEN;
      this.inputEl.placeholder = 'kanal oder spruch';
      this.inputEl.autocomplete = 'off';
      this.inputEl.spellcheck = false;
      this.inputEl.className = 'hidden';
      this.root.append(this.chEl, this.msgsEl, this.inputEl);

      this.inputEl.addEventListener('keydown', (e) => this._onInputKey(e, ctx));
      this.inputEl.addEventListener('blur', () => this._closeTune(ctx, false));
    }

    if (ctx.net) {
      ctx.net.on('radioRx', (msg) => this._onRx(msg));
    }

    this._render(true);
  }

  update(dt, ctx) {
    this._now += dt;
    const input = ctx.input;

    if (input && !this.tuning) {
      if (input.justPressed('radio')) {
        this.powered = !this.powered;
        if (!this.powered) this._closeTune(ctx, false);
        // Server über aktuellen Kanal informieren (auch beim Einschalten).
        if (this.powered) ctx.net?.tuneRadio?.(this.channel);
        this._render(true);
      }
      if (this.powered && input.justPressed('tune')) {
        this._openTune(ctx);
      }
    }

    // Alte Sprüche ausblenden.
    const before = this.messages.length;
    pruneRadioMessages(this.messages, this._now, RADIO_MSG_TTL);
    if (this.messages.length !== before) this._render(true);

    // Rauschen: leise wenn an und Empfang da, körniger wenn Stille.
    const audio = ctx.systems?.audio;
    if (audio?.noise) {
      if (!this.powered) {
        this._noiseLevel = 0;
      } else {
        const recent = this.messages.length > 0
          && (this._now - this.messages[this.messages.length - 1].t) < 2.5;
        const target = recent ? 0.04 : 0.14;
        this._noiseLevel += (target - this._noiseLevel) * Math.min(1, dt * 3);
      }
      audio.noise(this._noiseLevel);
    }

    // Periodisch DOM nur bei Bedarf — _render ist ohnehin change-guarded.
    if (this.powered) this._render(false);
  }

  _onRx(msg) {
    if (!this.powered) return;
    // Server schickt ch/m/q (kurze Feldnamen) oder bereits gemappt.
    const channel = msg.ch ?? msg.channel;
    const text = msg.m ?? msg.text ?? '';
    const quality = msg.q ?? msg.quality ?? 0;
    if (!text || quality <= 0) return;
    if (channel != null && channel !== this.channel) return;

    pushRadioMessage(this.messages, {
      text,
      quality,
      channel: channel ?? this.channel,
    }, this._now);

    this._render(true);
  }

  _openTune(ctx) {
    if (!this.inputEl || !this.powered) return;
    this.tuning = true;
    this.inputEl.classList.remove('hidden');
    this.inputEl.value = '';
    if (ctx.input) ctx.input.textCapture = true;
    // Pointer-Lock löst sich beim Fokussieren ohnehin; Fokus verzögert,
    // damit der T-Key nicht ins Feld rutscht.
    setTimeout(() => this.inputEl?.focus(), 0);
    this._render(true);
  }

  _closeTune(ctx, keepFocus = false) {
    this.tuning = false;
    if (ctx?.input) ctx.input.textCapture = false;
    if (this.inputEl) {
      this.inputEl.classList.add('hidden');
      this.inputEl.value = '';
      if (!keepFocus) this.inputEl.blur();
    }
    this._render(true);
  }

  _onInputKey(e, ctx) {
    if (e.code === 'Escape') {
      e.preventDefault();
      this._closeTune(ctx);
      return;
    }
    if (e.code !== 'Enter') return;
    e.preventDefault();
    const raw = this.inputEl.value.trim();
    this._closeTune(ctx);
    if (!raw || !this.powered) return;

    if (looksLikeChannel(raw)) {
      const ch = parseChannel(raw);
      if (ch == null) {
        ctx.log?.('kanal unbekannt');
        return;
      }
      this.channel = ch;
      ctx.net?.tuneRadio?.(ch);
      ctx.log?.(`kanal ${formatChannel(ch)}`);
      this._render(true);
      return;
    }

    // Text senden.
    const text = raw.slice(0, RADIO_MESSAGE_MAX_LEN);
    ctx.net?.sendRadio?.(this.channel, text);
    // Eigenen Spruch lokal zeigen (Server schickt ihn nicht zurück an den Sender).
    pushRadioMessage(this.messages, {
      text,
      quality: 1,
      channel: this.channel,
    }, this._now);
    this._render(true);
  }

  _render(force) {
    if (!this.root) return;
    const label = this.powered
      ? formatChannel(this.channel) ?? '—'
      : 'aus';
    const sig = `${this.powered}|${label}|${this.tuning}|${this.messages.length}|${
      this.messages.map((m) => m.text).join('\n')
    }`;
    if (!force && sig === this._lastDomSig) return;
    this._lastDomSig = sig;

    if (this.chEl) {
      this.chEl.textContent = this.powered ? label : 'funk aus';
      this.chEl.className = this.powered ? 'ch' : 'ch off';
    }

    if (this.msgsEl) {
      // Nur neu bauen wenn sich Inhalt änderte — max RADIO_HISTORY, typisch ≪.
      this.msgsEl.textContent = '';
      for (let i = 0; i < this.messages.length; i++) {
        const m = this.messages[i];
        const div = document.createElement('div');
        div.className = m.quality < 0.45 ? 'msg weak' : 'msg';
        // quality steuert Opazität zusätzlich zur Klasse (feiner Verlauf).
        div.style.opacity = String(0.35 + 0.55 * Math.max(0, Math.min(1, m.quality)));
        div.textContent = m.text;
        this.msgsEl.appendChild(div);
      }
    }
  }

  dispose() {
    this.voice?.dispose();
    if (this.ctx?.input) this.ctx.input.textCapture = false;
  }
}
